// Copyright (c) 2023, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 Inc. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import { useEffect } from "react";
import clsx from "clsx";
import { styled, useTheme } from "@mui/material/styles";
import { makeStyles } from "@mui/styles";

import {
  Box,
  Button,
  Collapse,
  Divider,
  Drawer as MuiDrawer,
  Hidden,
  IconButton,
  Link,
  List,
  Typography,
} from "@mui/material";
import BuildIcon from "@mui/icons-material/Build";
import NavItem from "./NavItem";
import { APP_CONFIG, PRIVILEGES } from "../Config";
import { getGmailMailTo } from "../utils/formatting";
import { isAccessGranted } from "../utils/oauth";
import { ReactComponent as AllTravelSvg } from "../icons/alltravel.svg";
import { ReactComponent as TravelInvoices } from "../icons/travelinvoices.svg";
import { ReactComponent as PerDiems } from "../icons/PerDiems.svg";
import { ReactComponent as NewTravelSvg } from "../icons/newtravel.svg";
import { ReactComponent as TravelDocumentSvg } from "../icons/traveldocument.svg";
import { ReactComponent as VisaSvg } from "../icons/visa.svg";
import { ReactComponent as MyTravelRequests } from "../icons/myTravelRequests.svg";
import { ReactComponent as ProfileSvg } from "../icons/profile.svg";
import { ReactComponent as PreferenceSvg } from "../icons/preference.svg";
import { ReactComponent as ReportsSvg } from "../icons/report.svg";

const items = [
  {
    href: APP_CONFIG.PAGES.NEW_TRAVEL_REQUESTS,
    icon: () => <NewTravelSvg />,
    title: "New Travel Requests",
    checkPrivilege: true,
    privilege: [PRIVILEGES.NEW_TRAVEL_REQUESTS],
  },
  {
    href: APP_CONFIG.PAGES.ALL_TRAVEL_REQUESTS,
    icon: () => <AllTravelSvg />,
    title: "All Travel Requests",
    checkPrivilege: true,
    privilege: [
      PRIVILEGES.ALL_TRAVEL_REQUESTS,
      PRIVILEGES.AGENT_TRAVEL_REQUESTS,
    ],
  },
  {
    href: APP_CONFIG.PAGES.ALL_INVOICES,
    icon: () => <TravelInvoices />,
    title: "All Invoices",
    checkPrivilege: true,
    privilege: [PRIVILEGES.VIEW_ALL_INVOICES_PAGE],
  },
  {
    href: APP_CONFIG.PAGES.ALL_PER_DIEM_PAYMENTS,
    icon: () => <PerDiems />,
    title: "All Per Diem Payments",
    checkPrivilege: true,
    privilege: [PRIVILEGES.VIEW_ALL_PER_DIEM_PAYMENTS_PAGE],
  },
  {
    href: APP_CONFIG.PAGES.VIEW_TRAVEL_DOCUMENT_LIST,
    icon: () => <TravelDocumentSvg />,
    title: "Travel Documents",
    checkPrivilege: true,
    privilege: [PRIVILEGES.VIEW_TRAVEL_DOCUMENT_LIST],
  },
  {
    href: APP_CONFIG.PAGES.VIEW_VISA_LIST,
    icon: () => <VisaSvg />,
    title: "Visa Details",
    checkPrivilege: true,
    privilege: [PRIVILEGES.VIEW_VISA_LIST],
  },
  {
    href: APP_CONFIG.PAGES.MY_TRAVEL_REQUESTS,
    icon: () => <MyTravelRequests />,
    title: "My Travel Requests",
    checkPrivilege: true,
    privilege: [PRIVILEGES.MY_TRAVEL_REQUESTS],
  },
  {
    href: APP_CONFIG.PAGES.REPORTS,
    icon: () => <ReportsSvg />,
    title: "Reports",
    checkPrivilege: false,
    privilege: [PRIVILEGES.VIEW_ALL_INVOICES_PAGE, PRIVILEGES.VIEW_ALL_PER_DIEM_PAYMENTS_PAGE],
  },
  {
    href: APP_CONFIG.PAGES.PROFILE,
    icon: () => <ProfileSvg />,
    title: "Profile",
    checkPrivilege: false,
    privilege: [PRIVILEGES.VIEW_PROFILE],
  },
  {
    href: APP_CONFIG.PAGES.SETTINGS,
    icon: () => <PreferenceSvg />,
    title: "Settings",
    checkPrivilege: false,
    privilege: [PRIVILEGES.APP_PREFERENCES],
  },
];

const drawerWidth = 240;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
  },

  drawer: {
    width: drawerWidth,
    zIndex: 500,
    flexShrink: 0,
    whiteSpace: "nowrap",
  },
  drawerOpen: {
    width: drawerWidth,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    overflow: "clip",
  },
  drawerClose: {
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    overflow: "clip",
    width: theme.spacing(7) + 1,
    [theme.breakpoints.up("sm")]: {
      width: theme.spacing(9) + 1,
    },
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
  },
}));

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflow: "clip",
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  overflow: "clip",
  width: `calc(${theme.spacing(8)})`,
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
  zIndex: 1,
}));

export default function MiniDrawer({ open, hoverActive, page, ...rest }) {
  const classes = useStyles();

  return (
    <Drawer
      variant="permanent"
      open={open}
      className={clsx(classes.drawer, {
        [classes.drawerOpen]: open,
        [classes.drawerClose]: !open,
      })}
      classes={{
        paper: clsx({
          [classes.drawerOpen]: open,
          [classes.drawerClose]: !open,
        }),
      }}
    >
      <DrawerHeader></DrawerHeader>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        <Divider />
        <Box sx={{ pt: 1, pl: 1 }}>
          <List>
            {items.map((item, index) => {
              return (
                isAccessGranted(item.privilege) && (
                  <NavItem
                    selected={page && page.startsWith(item.href)}
                    href={item.href}
                    key={item.title}
                    title={item.title}
                    icon={item.icon}
                  />
                )
              );
            })}
          </List>
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        <Collapse
          in={open}
          orientation="horizontal"
          classes={{ wrapperInner: { width: "100%" } }}
          timeout={2}
        >
          <Box
            sx={{
              backgroundColor: "background.default",
              m: 2,
              p: 2,
            }}
          >
            <Typography align="center" gutterBottom variant="h4" noWrap>
              Need help?
            </Typography>
            <Typography align="center" variant="body2" noWrap>
              Contact Internal Apps
            </Typography>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                pt: 2,
              }}
            >
              <Link
                href={getGmailMailTo(
                  APP_CONFIG.EMAILS.GET_HELP_EMAIL_TO,
                  APP_CONFIG.EMAILS.GET_HELP_EMAIL_SUBJECT
                )}
                target="_blank"
                rel="noreferrer"
              >
                <Button
                  color="primary"
                  variant="contained"
                  style={{ textTransform: "none" }}
                >
                  Get Help
                </Button>
              </Link>
            </Box>
          </Box>
        </Collapse>
      </Box>
    </Drawer>
  );
}
